import { logger } from '@/utils/logger/logger';
import { Component, type ReactNode } from 'react';

interface HomePreviewErrorBoundaryProps {
  children: ReactNode;
}

interface HomePreviewErrorBoundaryState {
  hasError: boolean;
}

/**
 * Local containment for the Home hover preview's content: a future failure
 * anywhere inside it (a missing provider like the `SavedChatsProvider` gap
 * this shipped with, a bad row shape, anything else) renders nothing inside
 * the popover instead of reaching the app's own page-level `ErrorBoundary`
 * and replacing the whole app with its "Oops, there was an error!" fallback.
 * Recent chat titles and visit labels can be interpolated into third-party
 * error messages, so this boundary never hands the raw Error or component
 * stack to telemetry. The stable classification below keeps the failure
 * observable without exporting user content.
 */
export class HomePreviewErrorBoundary extends Component<
  HomePreviewErrorBoundaryProps,
  HomePreviewErrorBoundaryState
> {
  state: HomePreviewErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): HomePreviewErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    logger.error('home_hover_preview_render_failed', {
      surface: 'home-hover-preview',
      errorName: error.name,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}
